import React, { useState } from 'react';
import {
  BarChart,
  Filter,
  ChevronDown,
  Download,
  Loader2,
  CalendarDays,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { showToast } from '../../utils/toast';

interface SettlementReportFilters {
  dateRange: {
    from: string | null;
    to: string | null;
  };
}

const SettlementReport: React.FC = () => {
  const today = new Date().toISOString().split('T')[0];

  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] =
    useState<SettlementReportFilters>({
      dateRange: {
        from: null,
        to: null,
      },
    });

  const [tempFilters, setTempFilters] =
    useState<SettlementReportFilters>({
      dateRange: {
        from: null,
        to: null,
      },
    });

  const [filterError, setFilterError] =
    useState<string | null>(null);

  const handleDateChange = (
    key: 'from' | 'to',
    value: string
  ) => {
    setFilterError(null);

    setTempFilters((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [key]: value || null,
      },
    }));
  };

  const handleResetFilters = () => {
    const resetFilters: SettlementReportFilters = {
      dateRange: {
        from: null,
        to: null,
      },
    };

    setTempFilters(resetFilters);
    setFilters(resetFilters);
    setFilterError(null);
  };

  const handleApplyFilters = () => {
    const { from, to } = tempFilters.dateRange;

    if (!from || !to) {
      setFilterError(
        'Please select both From Date and To Date.'
      );
      return;
    }

    if (from > to) {
      setFilterError(
        'From Date cannot be later than To Date.'
      );
      return;
    }

    setFilters(tempFilters);
    setShowFilters(false);
    setFilterError(null);
  };

  const downloadReport = async () => {
    if (
      !filters.dateRange.from ||
      !filters.dateRange.to
    ) {
      showToast.error(
        'Please select a date range before downloading.'
      );
      return;
    }

    try {
      setIsLoading(true);

      /*
       * API integration will be added later.
       * For now, this is only a placeholder.
       */

      await new Promise((resolve) =>
        setTimeout(resolve, 1000)
      );

      showToast.success(
        'Settlement report downloaded successfully'
      );
    } catch (error) {
      console.error(
        'Error downloading settlement report:',
        error
      );

      showToast.error(
        'Failed to download settlement report'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const downloadTodaysReport = async () => {
    try {
      setIsLoading(true);

      /*
       * API integration will be added later.
       * For now, this is only a placeholder.
       */

      await new Promise((resolve) =>
        setTimeout(resolve, 1000)
      );

      showToast.success(
        "Today's settlement report downloaded successfully"
      );
    } catch (error) {
      console.error(
        "Error downloading today's settlement report:",
        error
      );

      showToast.error(
        "Failed to download today's settlement report"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <BarChart className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />

          <h1 className="text-xl sm:text-2xl font-gilroy-bold text-gray-800">
            Settlement Report
          </h1>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          {/* Filters */}
          <Button
            variant="outline"
            onClick={() =>
              setShowFilters((prev) => !prev)
            }
            className="flex items-center justify-center"
            size="sm"
          >
            <Filter size={14} className="mr-1" />
            Filters
            <ChevronDown
              size={14}
              className="ml-1"
            />
          </Button>

          {/* Today's Report */}
          <Button
            variant="outline"
            onClick={downloadTodaysReport}
            disabled={isLoading}
            className="flex items-center justify-center"
            size="sm"
          >
            {isLoading ? (
              <Loader2 className="animate-spin w-4 h-4 mr-1" />
            ) : (
              <CalendarDays
                size={14}
                className="mr-1"
              />
            )}

            Today's Report
          </Button>

          {/* Download Report */}
          <Button
            onClick={downloadReport}
            disabled={
              isLoading ||
              !filters.dateRange.from ||
              !filters.dateRange.to
            }
            className="flex items-center justify-center"
            size="sm"
          >
            {isLoading ? (
              <Loader2 className="animate-spin w-4 h-4 mr-1" />
            ) : (
              <Download
                size={14}
                className="mr-1"
              />
            )}

            {isLoading
              ? 'Downloading...'
              : 'Download Report'}
          </Button>
        </div>
      </div>

      {/* Applied Date Range */}
      {filters.dateRange.from &&
        filters.dateRange.to && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded bg-blue-100 text-blue-700 font-medium">
              <CalendarDays size={12} />

              {filters.dateRange.from} -{' '}
              {filters.dateRange.to}
            </span>
          </div>
        )}

      {/* Filters Section */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-4">
            {/* From Date */}
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Date
              </label>

              <input
                type="date"
                value={
                  tempFilters.dateRange.from || ''
                }
                max={
                  tempFilters.dateRange.to || today
                }
                onChange={(e) =>
                  handleDateChange(
                    'from',
                    e.target.value
                  )
                }
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* To Date */}
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To Date
              </label>

              <input
                type="date"
                value={
                  tempFilters.dateRange.to || ''
                }
                min={
                  tempFilters.dateRange.from || undefined
                }
                max={today}
                onChange={(e) =>
                  handleDateChange(
                    'to',
                    e.target.value
                  )
                }
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Quick Today's Report */}
            <div className="md:col-span-6 flex items-end">
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => {
                  setTempFilters({
                    dateRange: {
                      from: today,
                      to: today,
                    },
                  });

                  setFilterError(null);
                }}
              >
                <CalendarDays
                  size={14}
                  className="mr-1.5"
                />
                Select Today's Date
              </Button>
            </div>

            {/* Filter Actions */}
            <div className="md:col-span-12 flex flex-col items-end mt-2 space-y-2">
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  onClick={handleResetFilters}
                  size="sm"
                >
                  Reset
                </Button>

                <Button
                  onClick={handleApplyFilters}
                  size="sm"
                >
                  Apply Filters
                </Button>
              </div>

              {filterError && (
                <div className="text-xs text-red-500 text-right w-full sm:w-auto">
                  {filterError}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <p className="text-sm sm:text-base">
          Select a date range and click{' '}
          <b>Download Report</b> to download the
          settlement report.
        </p>
      </div>
    </div>
  );
};

export default SettlementReport;